/**
 * Varifrån en kund kom: säljlänkar med UTM-kod, UTM-koden som följer med
 * ordern, och Google Analytics (laddas bara efter samtycke).
 *
 * Appen använder hash-routing (#/p/slug). Allt efter # syns inte för
 * statistikverktyg, så UTM-koden ligger före: https://…/?utm_…#/p/slug
 */

export const UTM_CAMPAIGN = 'klassforening';

/** Var QR-koden sitter. Blir utm_medium. */
export type QrSurface = 'saljkort' | 'helskarm' | 'flygblad' | 'katalog';

/** Vem som skickade en länk (utan QR-kod). Blir utm_medium. */
export type LinkChannel = 'delad' | 'admin';

export interface Utm {
    source?: string;
    medium?: string;
    campaign?: string;
    content?: string;
}

const UTM_KEYS = ['source', 'medium', 'campaign', 'content'] as const;
const UTM_VALUE = /^[a-z0-9._-]{1,64}$/i;

const storeLink = (base: string, slug: string, source?: string, medium?: string): string => {
    const cleanBase = base.endsWith('/') ? base.slice(0, -1) : base;
    if (!source || !medium) return `${cleanBase}/#/p/${slug}`;
    const q = new URLSearchParams({ utm_source: source, utm_medium: medium, utm_campaign: UTM_CAMPAIGN, utm_content: slug });
    return `${cleanBase}/?${q.toString()}#/p/${slug}`;
};

/** Säljarens butik. Utan yta: den korta länken att visa och skriva ut. Med yta: QR-kodens länk. */
export const sellerLink = (base: string, slug: string, surface?: QrSurface): string =>
    storeLink(base, slug, surface ? 'qr' : undefined, surface);

/**
 * Länken som delas eller kopieras (sms, WhatsApp, sociala medier). Märkt som
 * länk i stället för QR-kod, så att köpen inte hamnar under "Direkt eller okänd".
 */
export const sharedSellerLink = (base: string, slug: string, channel: LinkChannel = 'delad'): string =>
    storeLink(base, slug, 'lank', channel);

/** Bara kända nycklar med säkra värden — resten slängs. */
export const cleanUtm = (raw: unknown): Utm | null => {
    if (!raw || typeof raw !== 'object') return null;
    const out: Utm = {};
    for (const key of UTM_KEYS) {
        const v = (raw as Record<string, unknown>)[key];
        if (typeof v === 'string' && UTM_VALUE.test(v.trim())) out[key] = v.trim().toLowerCase();
    }
    return out.source || out.medium || out.campaign ? out : null;
};

const UTM_STORAGE = 'cn_utm';
const UTM_TTL = 7 * 24 * 60 * 60 * 1000;

/** Läs UTM-koden ur adressen när besökaren kommer in och spara den en vecka. */
export const captureUtm = (search: string = typeof window !== 'undefined' ? window.location.search : ''): void => {
    const params = new URLSearchParams(search);
    const utm = cleanUtm({
        source: params.get('utm_source') ?? undefined,
        medium: params.get('utm_medium') ?? undefined,
        campaign: params.get('utm_campaign') ?? undefined,
        content: params.get('utm_content') ?? undefined,
    });
    if (!utm) return;
    try { localStorage.setItem(UTM_STORAGE, JSON.stringify({ ...utm, at: Date.now() })); } catch { /* lagring blockerad */ }
};

/** Säljkoden i butikens adress (#/p/kod), om sidan är en butik. */
export const storeSlugFromHash = (hash: string = typeof window !== 'undefined' ? window.location.hash : ''): string | null => {
    const m = hash.match(/^#\/p\/([^/?#]+)/);
    return m ? decodeURIComponent(m[1]).toLowerCase() : null;
};

/**
 * UTM-koden som följer med nästa order, om besöket kom via en sådan länk.
 * QR-koderna har säljarens kod i utm_content. Köper kunden i en annan
 * säljares butik än den QR-koden ledde till följer koden inte med, så att
 * ordern inte märks med fel säljares QR-kod.
 */
export const storedUtm = (storeSlug: string | null = storeSlugFromHash(), now: number = Date.now()): Utm | null => {
    try {
        const raw = JSON.parse(localStorage.getItem(UTM_STORAGE) || 'null');
        if (!raw || typeof raw.at !== 'number' || now - raw.at > UTM_TTL) return null;
        const utm = cleanUtm(raw);
        if (utm?.content && storeSlug && utm.content.toLowerCase() !== storeSlug.toLowerCase()) return null;
        return utm;
    } catch { return null; }
};

const SURFACE_LABELS: Record<string, string> = { saljkort: 'säljkort', helskarm: 'helskärm', flygblad: 'reklamblad', etikett: 'etikett', katalog: 'katalog', delad: 'delad av säljaren', admin: 'skickad av admin' };
const SOURCE_LABELS: Record<string, string> = { qr: 'QR', lank: 'Länk' };

/** "QR · reklamblad" för admin. */
export const describeUtm = (utm?: Utm | null): string | null => {
    if (!utm) return null;
    const source = (utm.source && SOURCE_LABELS[utm.source]) || utm.source || 'kampanj';
    const medium = utm.medium ? SURFACE_LABELS[utm.medium] || utm.medium : null;
    return medium ? `${source} · ${medium}` : source;
};

// --- Google Analytics, bara efter samtycke ---

export const CONSENT_KEY = 'cn_cookie_consent';
export type Consent = 'necessary' | 'statistics';

let GA_ID: string = (import.meta as any).env?.VITE_GA_MEASUREMENT_ID || '';

/** Bara för tester: mätnings-id:t sätts annars vid bygget. */
export const setMeasurementIdForTests = (id: string): void => { GA_ID = id; };

/** Finns ett mätnings-id? Annars finns inget att samtycka till. */
export const analyticsConfigured = (): boolean => /^G-[A-Z0-9]+$/.test(GA_ID);

/** Tidigare version sparade 'true' (bara information): räknas som nödvändiga. */
export const readConsent = (): Consent | null => {
    try {
        const v = localStorage.getItem(CONSENT_KEY);
        if (v === 'statistics') return 'statistics';
        if (v === 'necessary' || v === 'true') return 'necessary';
        return null;
    } catch { return null; }
};

let gaLoaded = false;
/**
 * Googles egen kodsnutt: kommandona måste läggas i kön som arguments-objekt.
 * En vanlig lista (t.ex. [...args]) läses inte av gtag.js, och då skickas
 * inga sidvisningar eller händelser alls.
 */
function gtag(..._args: unknown[]): void {
    const w = window as any;
    w.dataLayer = w.dataLayer || [];
    // eslint-disable-next-line prefer-rest-params
    w.dataLayer.push(arguments);
}

/** Sidor som räknas: butiken, startsidan och infosidorna — aldrig inloggade vyer. */
const isPublicRoute = (hash: string) => !/^#\/(dashboard|update-password)/.test(hash);

let lastPageView = '';

/**
 * En sidvisning för den sida som visas nu. Anropas av routern vid varje
 * sidbyte (AnalyticsPageViews i App.tsx) och när statistiken slås på; samma
 * adress två gånger i rad räknas en gång.
 */
export const trackPageView = (): void => {
    if (!gaLoaded || !isPublicRoute(window.location.hash)) return;
    if (window.location.href === lastPageView) return;
    lastPageView = window.location.href;
    gtag('event', 'page_view', {
        page_location: window.location.href,
        page_path: window.location.pathname + window.location.hash,
        page_title: document.title,
    });
};

export const startAnalytics = (): void => {
    if (gaLoaded || !analyticsConfigured() || readConsent() !== 'statistics') return;
    gaLoaded = true;
    const s = document.createElement('script');
    s.async = true;
    s.src = `https://www.googletagmanager.com/gtag/js?id=${GA_ID}`;
    document.head.appendChild(s);
    gtag('js', new Date());
    // Sidvisningar skickas själva, med #-vägen, eftersom appen byter sida utan ny laddning.
    // Routern byter sida med pushState (ingen hashchange), så sidbytena kommer från AnalyticsPageViews.
    gtag('config', GA_ID, { send_page_view: false });
    trackPageView();
};

export const setConsent = (consent: Consent): void => {
    try { localStorage.setItem(CONSENT_KEY, consent); } catch { /* lagring blockerad */ }
    if (consent === 'statistics') startAnalytics();
};

/** En produkt i GA4:s e-handelsformat. Inga personuppgifter, bara produkten. */
export interface GaItem { item_id: string; item_name: string; item_variant?: string; price: number; quantity: number }

const trackEcommerce = (name: string, items: GaItem[]): void => {
    if (!gaLoaded || items.length === 0) return;
    const value = Math.round(items.reduce((s, i) => s + i.price * i.quantity, 0) * 100) / 100;
    gtag('event', name, { currency: 'SEK', value, items });
};

/** Kunden öppnar en produktsida. */
export const trackViewItem = (item: GaItem): void => trackEcommerce('view_item', [item]);
/** Kunden lägger en produkt i varukorgen. */
export const trackAddToCart = (item: GaItem): void => trackEcommerce('add_to_cart', [item]);
/** Kunden öppnar varukorgen med kassan. */
export const trackBeginCheckout = (items: GaItem[]): void => trackEcommerce('begin_checkout', items);

/** Genomfört köp till Google Analytics (bara om det är igång). */
export const trackPurchase = (order: { orderId: string; totalAmount: number | null; items: { name: string; variantName?: string | null; quantity: number; price: number }[] }): void => {
    if (!gaLoaded) return;
    gtag('event', 'purchase', {
        transaction_id: order.orderId,
        value: order.totalAmount ?? undefined,
        currency: 'SEK',
        items: order.items.map(i => ({ item_name: i.name, item_variant: i.variantName || undefined, quantity: i.quantity, price: i.price })),
    });
};
